import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminNav from '@/components/admin/AdminNav';
import Footer from '@/components/Footer';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Search, Filter, Eye, Calendar, Download, Mail, Phone } from 'lucide-react';
import { format } from 'date-fns';
import SEO from '@/components/SEO';

interface Booking {
  id: string;
  booking_id: string;
  full_name: string;
  email: string;
  phone: string;
  service: string;
  appointment_type: string;
  appointment_start: string | null;
  payment_method: string;
  payment_status: string;
  created_at: string;
  situation: string;
  language: string;
}

const AdminBookings = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('bookings')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setBookings(data || []);
    } catch (error: any) {
      console.error('Error fetching bookings:', error);
      toast({
        title: 'Error',
        description: 'Failed to load bookings',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredBookings = bookings.filter((booking) => {
    const matchesSearch =
      booking.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.booking_id.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesService = serviceFilter === 'all' || booking.service === serviceFilter;
    const matchesStatus = statusFilter === 'all' || booking.payment_status === statusFilter;

    return matchesSearch && matchesService && matchesStatus;
  });

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
      paid: 'default',
      pending: 'secondary',
      failed: 'destructive',
    };
    return (
      <Badge variant={variants[status] || 'outline'}>
        {status.toUpperCase()}
      </Badge>
    );
  };

  const exportToCSV = () => {
    const headers = ['Booking ID', 'Name', 'Email', 'Phone', 'Service', 'Type', 'Date', 'Payment', 'Status', 'Created'];
    const rows = filteredBookings.map(b => [
      b.booking_id,
      b.full_name,
      b.email,
      b.phone,
      b.service,
      b.appointment_type,
      b.appointment_start ? format(new Date(b.appointment_start), 'PPpp') : 'TBD',
      b.payment_method,
      b.payment_status,
      format(new Date(b.created_at), 'PPpp'),
    ]);

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bookings-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
  };

  const viewDetails = (booking: Booking) => {
    setSelectedBooking(booking);
    setShowDetails(true);
  };

  return (
    <>
      <SEO 
        title="Admin - Bookings Management"
        description="Manage consultation bookings"
      />
      <div className="min-h-screen flex flex-col bg-background">
        <AdminNav />
        
        <main className="flex-1 py-8">
          <div className="container mx-auto px-4">
            <div className="mb-8">
              <h1 className="text-3xl font-bold mb-2">Bookings Management</h1>
              <p className="text-muted-foreground">View and manage all consultation bookings</p>
            </div>

            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <div>
                    <CardTitle>All Bookings</CardTitle>
                    <CardDescription>{filteredBookings.length} bookings found</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => navigate('/admin/dashboard')} variant="outline" size="sm">
                      Dashboard
                    </Button>
                    <Button onClick={exportToCSV} variant="outline" size="sm">
                      <Download className="h-4 w-4 mr-2" />
                      Export CSV
                    </Button>
                  </div>
                </div>
              </CardHeader>

              <CardContent>
                {/* Filters */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="relative">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by name, email, or booking ID..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9"
                    />
                  </div>

                  <Select value={serviceFilter} onValueChange={setServiceFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="Filter by service" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Services</SelectItem>
                      <SelectItem value="Immigration">Immigration</SelectItem>
                      <SelectItem value="Criminal Defense">Criminal Defense</SelectItem>
                      <SelectItem value="Personal Injury">Personal Injury</SelectItem>
                      <SelectItem value="Family Law">Family Law</SelectItem>
                      <SelectItem value="Tax">Tax</SelectItem>
                      <SelectItem value="Probate & Estate Planning">Probate & Estate</SelectItem>
                      <SelectItem value="Real Estate">Real Estate</SelectItem>
                      <SelectItem value="Business Law">Business Law</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="Filter by status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="paid">Paid</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="failed">Failed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Table */}
                {loading ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                  </div>
                ) : filteredBookings.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-muted-foreground">No bookings found</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Booking ID</TableHead>
                          <TableHead>Client</TableHead>
                          <TableHead>Service</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Payment</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredBookings.map((booking) => (
                          <TableRow key={booking.id}>
                            <TableCell className="font-mono text-sm">
                              {booking.booking_id}
                            </TableCell>
                            <TableCell>
                              <div>
                                <p className="font-medium">{booking.full_name}</p>
                                <p className="text-sm text-muted-foreground">{booking.email}</p>
                              </div>
                            </TableCell>
                            <TableCell>{booking.service}</TableCell>
                            <TableCell>{booking.appointment_type}</TableCell>
                            <TableCell>
                              {booking.appointment_start
                                ? format(new Date(booking.appointment_start), 'MMM dd, yyyy')
                                : 'TBD'}
                            </TableCell>
                            <TableCell>{booking.payment_method}</TableCell>
                            <TableCell>{getStatusBadge(booking.payment_status)}</TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => viewDetails(booking)}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </main>

        <Footer />
      </div>

      {/* Booking Details Dialog */}
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Booking Details</DialogTitle>
            <DialogDescription>
              Full information for booking {selectedBooking?.booking_id}
            </DialogDescription>
          </DialogHeader>

          {selectedBooking && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Client Name</p>
                  <p className="font-semibold">{selectedBooking.full_name}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Language</p>
                  <p className="font-semibold uppercase">{selectedBooking.language}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">Email</p>
                  <a href={`mailto:${selectedBooking.email}`} className="text-primary hover:underline flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    {selectedBooking.email}
                  </a>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">Phone</p>
                  <a href={`tel:${selectedBooking.phone}`} className="text-primary hover:underline flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    {selectedBooking.phone}
                  </a>
                </div>
              </div>

              <div>
                <p className="text-sm font-medium text-muted-foreground mb-2">Service & Appointment</p>
                <div className="bg-muted p-4 rounded-lg space-y-2">
                  <p><strong>Service:</strong> {selectedBooking.service}</p>
                  <p><strong>Type:</strong> {selectedBooking.appointment_type}</p>
                  {selectedBooking.appointment_start && (
                    <p><strong>Scheduled:</strong> {format(new Date(selectedBooking.appointment_start), 'PPpp')}</p>
                  )}
                </div>
              </div>

              <div>
                <p className="text-sm font-medium text-muted-foreground mb-2">Payment Information</p>
                <div className="bg-muted p-4 rounded-lg space-y-2">
                  <p><strong>Method:</strong> {selectedBooking.payment_method}</p>
                  <p><strong>Status:</strong> {getStatusBadge(selectedBooking.payment_status)}</p>
                  <p><strong>Amount:</strong> $150.00 USD</p>
                </div>
              </div>

              <div>
                <p className="text-sm font-medium text-muted-foreground mb-2">Situation Description</p>
                <div className="bg-muted p-4 rounded-lg">
                  <p className="text-sm whitespace-pre-wrap">{selectedBooking.situation}</p>
                </div>
              </div>

              <div className="text-sm text-muted-foreground">
                <p>Booking created: {format(new Date(selectedBooking.created_at), 'PPpp')}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AdminBookings;
